import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  pointerWithin,
  rectIntersection
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import Button from '@atlaskit/button'
import AddIcon from '@atlaskit/icon/glyph/add'
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line'
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs'
import {
  fetchFolderTreeAsync,
  fetchAllScenariosAsync,
  fetchScenariosInFolderAsync,
  deleteFolderAsync,
  deleteTestScenarioAsync,
  reorderScenariosAsync,
  reorderScenariosOptimistic,
  reorderFoldersAsync,
  moveScenarioToFolderAsync,
  moveFolderAsync,
  cloneFolderAsync,
  cloneTestScenarioAsync,
  selectFolderTree,
  selectFolders,
  selectScenarios,
  selectSelectedFolderId,
  selectFoldersLoading,
  selectScenariosLoading,
  selectFoldersError,
  selectFolder,
  clearFolders,
  setExpandedFolderIds,
  selectExpandedFolderIds
} from '../../store/slices/folderSlice'
import { useNotification } from '../../contexts/NotificationContext'
import {
  fetchReleaseByIdAsync,
  selectCurrentRelease,
  selectCurrentReleaseLoading
} from '../../store/slices/releaseSlice'
import { setNavigationContext } from '../../store/slices/navigationContextSlice'
import { buildFolderPath } from '../../utils/folderUtils'
import { FolderSidebar } from './FolderSidebar'
import { ScenarioPanel } from './ScenarioPanel'
import { CreateFolderModal } from './CreateFolderModal'
import { CreateScenarioModal } from './CreateScenarioModal'
import { EditFolderModal } from './EditFolderModal'
import { EditScenarioModal } from './EditScenarioModal'
import { MoveScenarioModal } from './MoveScenarioModal'
import { ConfirmDeleteModal } from './ConfirmDeleteModal'
import { ImportFromLibraryModal } from '../Library/ImportFromLibraryModal'
import { ImportFromReleaseModal } from './ImportFromReleaseModal'
import { FolderBrowserSkeleton } from '../common/FolderBrowserSkeleton'
import ReportModal from '../reports/ReportModal'

/**
 * FOLDER BROWSER PAGE
 *
 * Two-panel layout for browsing folders and scenarios within a release.
 * - Left panel: Folder tree with expand/collapse
 * - Right panel: Scenarios in the selected folder
 *
 * Features:
 * - "All Scenarios" view shows all scenarios across folders (including unfoldered ones)
 * - Click folder to select (shows scenarios in right panel)
 * - Click chevron to expand/collapse sub-folders
 * - Create, edit, delete folders and scenarios
 * - Move scenarios between folders
 */
function FolderBrowser() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { releaseId } = useParams()
  const { selectedProject } = useOutletContext()
  const [searchParams, setSearchParams] = useSearchParams()

  // Redux state
  const folderTree = useSelector(selectFolderTree)
  const folders = useSelector(selectFolders)
  const scenarios = useSelector(selectScenarios)
  const selectedFolderId = useSelector(selectSelectedFolderId)
  const selectedFolderIdRef = useRef(selectedFolderId)
  const expandedFolderIds = useSelector(selectExpandedFolderIds)
  const expandedFolderIdsRef = useRef(expandedFolderIds)
  const foldersLoading = useSelector(selectFoldersLoading)
  const scenariosLoading = useSelector(selectScenariosLoading)
  const error = useSelector(selectFoldersError)
  const currentRelease = useSelector(selectCurrentRelease)
  const releaseLoading = useSelector(selectCurrentReleaseLoading)

  // Notification context for success/error messages
  const { showSuccess, showError } = useNotification()

  // =========================================================================
  // MODAL STATE
  // =========================================================================

  // Create modals
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false)
  const [isCreateScenarioModalOpen, setIsCreateScenarioModalOpen] = useState(false)
  const [createFolderParentId, setCreateFolderParentId] = useState(null)

  // Edit modals
  const [isEditFolderModalOpen, setIsEditFolderModalOpen] = useState(false)
  const [editingFolder, setEditingFolder] = useState(null)
  const [isEditScenarioModalOpen, setIsEditScenarioModalOpen] = useState(false)
  const [editingScenario, setEditingScenario] = useState(null)

  // Delete modals
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false)
  const [deletingFolder, setDeletingFolder] = useState(null)
  const [isDeleteScenarioModalOpen, setIsDeleteScenarioModalOpen] = useState(false)
  const [deletingScenario, setDeletingScenario] = useState(null)

  // Move modal
  const [isMoveScenarioModalOpen, setIsMoveScenarioModalOpen] = useState(false)
  const [movingScenario, setMovingScenario] = useState(null)

  // Import from Library modal
  const [isImportFromLibraryModalOpen, setIsImportFromLibraryModalOpen] = useState(false)
  const [importTargetFolderId, setImportTargetFolderId] = useState(null)

  // Import from Release modal
  const [isImportFromReleaseModalOpen, setIsImportFromReleaseModalOpen] = useState(false)
  const [importFromReleaseTargetFolderId, setImportFromReleaseTargetFolderId] = useState(null)

  // Release report modal — opened from the page header. Matches the pattern
  // used on the release list page (project report) and scenario detail page
  // (scenario report); the scope is fixed by initialLevel, so the ReportModal
  // opens straight into the release view.
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const handleOpenReleaseReport = () => setIsReportModalOpen(true)
  const handleCloseReportModal = () => setIsReportModalOpen(false)

  // =========================================================================
  // DRAG AND DROP CONFIGURATION
  // =========================================================================

  // Configure drag sensors with activation distance to differentiate from clicks
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5 // 5px movement required before drag starts
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  /**
   * UNIFIED DRAG END HANDLER
   *
   * Handles all drag end events across the folder browser:
   * 1. Scenario dropped on a folder → Move scenario to that folder
   * 2. Folder dropped on a folder → Move folder to become child of target folder
   *    (only when dropped in CENTER of target folder)
   * 3. Scenario reordering within panel → Reorder scenarios
   * 4. Folder reordering in sidebar → Reorder folders
   *    (when dropped on TOP/BOTTOM edge of sibling folder)
   */
  const handleUnifiedDragEnd = (event) => {
    const { active, over } = event

    if (!over) return

    const activeId = String(active.id)
    const overId = String(over.id)

    // Helper to check if dragged item is a scenario
    const isScenarioDrag = activeId.startsWith('scenario-')
    // Helper to check if dragged item is a folder
    const isFolderDrag = activeId.startsWith('folder-') && !activeId.startsWith('folder-drop-')
    // Helper to check if drop target is a folder drop zone
    const isFolderDropTarget = overId.startsWith('folder-drop-')

    // Helper to determine drop position within target folder
    // Returns 'top' (insert before), 'center' (nest as child), or 'bottom' (insert after)
    const getDropIntent = () => {
      if (!over.rect || !event.activatorEvent) return 'center'

      const overRect = over.rect
      const pointerY = event.activatorEvent.clientY + (event.delta?.y || 0)

      const relativeY = pointerY - overRect.top
      const percentY = relativeY / overRect.height

      // Top 30% = insert before, bottom 30% = insert after, center 40% = nest
      if (percentY < 0.3) return 'top'
      if (percentY > 0.7) return 'bottom'
      return 'center'
    }

    // =========================================================================
    // CASE 0: Scenario dropped on "All Scenarios" → Clear folder (move to root)
    // =========================================================================
    if (isScenarioDrag && overId === 'all-scenarios-drop') {
      const scenarioId = activeId.replace('scenario-', '')
      const draggedScenario = scenarios.find(s => String(s.id) === scenarioId)

      if (!draggedScenario) return

      // No-op if scenario is already at root
      if (draggedScenario.folderId == null) return

      dispatch(moveScenarioToFolderAsync({
        testScenarioId: parseInt(scenarioId, 10),
        moveData: { folderId: null }
      })).unwrap().then(() => {
        // Refresh scenarios for the current folder view
        if (selectedFolderId !== null) {
          dispatch(fetchScenariosInFolderAsync(selectedFolderId))
        } else {
          dispatch(fetchAllScenariosAsync(releaseId))
        }
        // Refresh folder tree to update scenario counts
        dispatch(fetchFolderTreeAsync(releaseId))
      }).catch((error) => {
        const message = error?.message || 'Failed to move scenario. Please try again.'
        showError('Move Failed', message)
        dispatch(fetchFolderTreeAsync(releaseId))
      })
      return
    }

    // =========================================================================
    // CASE 1: Scenario dropped on a folder → Move scenario to that folder
    // =========================================================================
    if (isScenarioDrag && isFolderDropTarget) {
      const scenarioId = activeId.replace('scenario-', '')
      const targetFolderId = overId.replace('folder-drop-', '')

      // Get the scenario that was dragged
      const draggedScenario = scenarios.find(s => String(s.id) === scenarioId)

      if (draggedScenario && targetFolderId) {
        // Don't move if dropping on the same folder
        if (String(draggedScenario.folderId) === targetFolderId) {
          return
        }

        console.log('Moving scenario to folder:', { scenarioId, targetFolderId })

        // Dispatch move action
        dispatch(moveScenarioToFolderAsync({
          testScenarioId: parseInt(scenarioId, 10),
          moveData: {
            folderId: parseInt(targetFolderId, 10)
          }
        })).unwrap().then(() => {
          // Refresh scenarios for the current folder view
          if (selectedFolderId !== null) {
            dispatch(fetchScenariosInFolderAsync(selectedFolderId))
          }
          // Refresh folder tree to update scenario counts
          dispatch(fetchFolderTreeAsync(releaseId))
        }).catch((error) => {
          const message = error?.message || 'Failed to move scenario. Please try again.'
          showError('Move Failed', message)
          dispatch(fetchFolderTreeAsync(releaseId))
        })
      }
      return
    }

    // =========================================================================
    // CASE 2: Folder dropped on a folder → Move or Reorder based on drop position
    // - Drop on CENTER (40%) → Move folder to become child of target (nest)
    // - Drop on EDGE (top/bottom 30%) + same parent → Reorder siblings
    // - Drop on EDGE (top/bottom 30%) + different parent → Move to target's parent level (promote/demote)
    // =========================================================================
    if (isFolderDrag && isFolderDropTarget) {
      const draggedFolderId = activeId.replace('folder-', '')
      const targetFolderId = overId.replace('folder-drop-', '')

      // Don't allow dropping folder on itself
      if (draggedFolderId === targetFolderId) {
        return
      }

      // Find the dragged and target folders
      const draggedFolder = folders.find(f => String(f.id) === draggedFolderId)
      const targetFolder = folders.find(f => String(f.id) === targetFolderId)

      if (!draggedFolder || !targetFolder) return

      // Check drop intent based on vertical position
      const dropIntent = getDropIntent()
      console.log('Drop intent:', dropIntent, 'dragged:', draggedFolderId, 'target:', targetFolderId)

      // If dropping on edge (top/bottom 30%) → REORDER or MOVE to target's parent level
      if (dropIntent === 'top' || dropIntent === 'bottom') {
        const sameParent = String(draggedFolder.parentId || '') === String(targetFolder.parentId || '')

        if (sameParent) {
          // Same parent → reorder siblings
          console.log('Reordering folders (same parent)')

          // Find siblings (folders with same parent)
          const parentId = draggedFolder.parentId
          let siblings
          if (parentId === null) {
            // Root level folders
            siblings = folderTree
          } else {
            // Find parent and get its children
            const parentFolder = folders.find(f => f.id === parentId)
            siblings = parentFolder?.children || []
          }

          const oldIndex = siblings.findIndex(f => String(f.id) === draggedFolderId)
          const newIndex = siblings.findIndex(f => String(f.id) === targetFolderId)

          if (oldIndex !== -1 && newIndex !== -1) {
            // Only send the dragged folder's new orderIndex to backend (1-based)
            // Backend will handle adjusting other folders' indices
            dispatch(reorderFoldersAsync([{
              id: draggedFolder.id,
              orderIndex: newIndex + 1
            }])).unwrap().then(() => {
              dispatch(fetchFolderTreeAsync(releaseId))
            }).catch((error) => {
              const message = error?.message || 'Failed to reorder folders. Please try again.'
              showError('Reorder Failed', message)
              dispatch(fetchFolderTreeAsync(releaseId))
            })
          }
          return
        } else {
          // Different parent → move folder to the target's parent level
          // This enables promoting a subfolder by dragging it to the edge of a folder at a higher level
          const newParentId = targetFolder.parentId

          console.log('Moving folder to different parent level:', {
            folderId: draggedFolderId,
            newParentId: newParentId,
            position: dropIntent
          })

          // Prevent moving a folder into its own descendant's parent (circular move)
          const isDescendantCheck = (folderId, targetId) => {
            const children = folders.filter(f => String(f.parentId) === folderId)
            for (const child of children) {
              if (String(child.id) === targetId) return true
              if (isDescendantCheck(String(child.id), targetId)) return true
            }
            return false
          }

          if (isDescendantCheck(draggedFolderId, targetFolderId)) {
            console.warn('Cannot move folder: target is a descendant')
            return
          }

          // Find target's siblings at the destination level to calculate orderIndex
          let destinationSiblings
          if (newParentId === null) {
            destinationSiblings = folderTree
          } else {
            const parentFolder = folders.find(f => f.id === newParentId)
            destinationSiblings = parentFolder?.children || []
          }

          // Calculate insertion index based on drop edge position (1-based to match backend)
          const targetIndex = destinationSiblings.findIndex(f => String(f.id) === targetFolderId)
          const orderIndex = dropIntent === 'bottom' ? targetIndex + 2 : targetIndex + 1

          dispatch(moveFolderAsync({
            folderId: parseInt(draggedFolderId, 10),
            moveData: {
              newParentId: newParentId !== null ? parseInt(String(newParentId), 10) : null,
              orderIndex: Math.max(1, orderIndex)
            }
          })).unwrap().then(() => {
            dispatch(fetchFolderTreeAsync(releaseId))
          }).catch((error) => {
            const message = error?.message || 'Failed to move folder. Please try again.'
            showError('Move Failed', message)
            dispatch(fetchFolderTreeAsync(releaseId))
          })
          return
        }
      }

      // Otherwise → NEST (move as child)
      // Don't move if target is the same as current parent
      if (String(draggedFolder.parentId) === targetFolderId) {
        return
      }

      // Helper to check if targetId is a descendant of folderId
      const isDescendant = (folderId, targetId) => {
        const children = folders.filter(f => String(f.parentId) === folderId)
        for (const child of children) {
          if (String(child.id) === targetId) return true
          if (isDescendant(String(child.id), targetId)) return true
        }
        return false
      }

      // Don't allow dropping a folder into its own descendant
      if (isDescendant(draggedFolderId, targetFolderId)) {
        console.warn('Cannot move folder into its own descendant')
        return
      }

      console.log('Moving folder to new parent:', { folderId: draggedFolderId, newParentId: targetFolderId })

      // Dispatch move action
      dispatch(moveFolderAsync({
        folderId: parseInt(draggedFolderId, 10),
        moveData: {
          newParentId: parseInt(targetFolderId, 10),
          orderIndex: 1 // Place at the beginning (1-based)
        }
      })).unwrap().then(() => {
        // Refresh folder tree
        dispatch(fetchFolderTreeAsync(releaseId))
      }).catch((error) => {
        const message = error?.message || 'Failed to move folder. Please try again.'
        showError('Move Failed', message)
        dispatch(fetchFolderTreeAsync(releaseId))
      })
      return
    }

    // =========================================================================
    // CASE 3: Scenario reordering within the same panel
    // =========================================================================
    if (isScenarioDrag && overId.startsWith('scenario-')) {
      const activeScenarioId = activeId.replace('scenario-', '')
      const overScenarioId = overId.replace('scenario-', '')

      if (activeScenarioId !== overScenarioId) {
        const oldIndex = scenarios.findIndex(s => String(s.id) === activeScenarioId)
        const newIndex = scenarios.findIndex(s => String(s.id) === overScenarioId)

        if (oldIndex !== -1 && newIndex !== -1) {
          // Calculate new order for optimistic UI update
          const reorderedScenarios = [...scenarios]
          const [removed] = reorderedScenarios.splice(oldIndex, 1)
          reorderedScenarios.splice(newIndex, 0, removed)

          // Create full reorder data for optimistic UI update (1-based to match backend)
          const reorderData = reorderedScenarios.map((scenario, index) => ({
            id: scenario.id,
            orderIndex: index + 1
          }))

          // Optimistically update UI with all scenarios' new indices
          dispatch(reorderScenariosOptimistic(reorderData))

          // Only send the dragged scenario's new orderIndex to backend
          // Backend will handle adjusting other scenarios' indices
          const draggedScenario = scenarios.find(s => String(s.id) === activeScenarioId)
          dispatch(reorderScenariosAsync([{
            id: draggedScenario.id,
            orderIndex: newIndex + 1
          }])).unwrap().catch((error) => {
            const message = error?.message || 'Failed to reorder scenarios. Please try again.'
            showError('Reorder Failed', message)
            // Revert optimistic update by refreshing from server
            if (selectedFolderId !== null) {
              dispatch(fetchScenariosInFolderAsync(selectedFolderId))
            }
          })
        }
      }
      return
    }

    // =========================================================================
    // CASE 4: Folder reordering within sidebar (same level)
    // =========================================================================
    if (isFolderDrag && overId.startsWith('folder-') && !overId.startsWith('folder-drop-')) {
      const activeFolderId = activeId.replace('folder-', '')
      const overFolderId = overId.replace('folder-', '')

      if (activeFolderId !== overFolderId) {
        const oldIndex = folderTree.findIndex(f => String(f.id) === activeFolderId)
        const newIndex = folderTree.findIndex(f => String(f.id) === overFolderId)

        if (oldIndex !== -1 && newIndex !== -1) {
          // Only send the dragged folder's new orderIndex to backend
          // Backend will handle adjusting other folders' indices
          dispatch(reorderFoldersAsync([{
            id: Number(activeFolderId),
            orderIndex: newIndex + 1
          }])).unwrap().then(() => {
            dispatch(fetchFolderTreeAsync(releaseId))
          }).catch((error) => {
            const message = error?.message || 'Failed to reorder folders. Please try again.'
            showError('Reorder Failed', message)
            dispatch(fetchFolderTreeAsync(releaseId))
          })
        }
      }
      return
    }
  }

  // =========================================================================
  // DATA FETCHING
  // =========================================================================

  // Fetch release and folder data when component mounts
  useEffect(() => {
    if (releaseId) {
      dispatch(fetchReleaseByIdAsync(releaseId))
      dispatch(fetchFolderTreeAsync(releaseId))
      dispatch(fetchAllScenariosAsync(releaseId))
    }

    // Cleanup when leaving
    return () => {
      dispatch(clearFolders())
    }
  }, [dispatch, releaseId])

  // =========================================================================
  // FOLDER EXPANSION STATE PERSISTENCE
  // =========================================================================

  // Restore expanded folder state from localStorage when entering a release
  useEffect(() => {
    if (releaseId) {
      const storageKey = `expanded-folders-${releaseId}`
      const savedExpandedIds = localStorage.getItem(storageKey)
      if (savedExpandedIds) {
        try {
          const parsed = JSON.parse(savedExpandedIds)
          if (Array.isArray(parsed)) {
            dispatch(setExpandedFolderIds(parsed))
          }
        } catch (e) {
          console.warn('Failed to parse saved folder expansion state:', e)
        }
      } else {
        // No saved state - start with empty (all collapsed)
        dispatch(setExpandedFolderIds([]))
      }
    }
  }, [releaseId, dispatch])

  // Save expanded folder state to localStorage when it changes
  useEffect(() => {
    if (releaseId && expandedFolderIds) {
      const storageKey = `expanded-folders-${releaseId}`
      localStorage.setItem(storageKey, JSON.stringify(expandedFolderIds))
    }
  }, [releaseId, expandedFolderIds])

  // Fetch scenarios when folder selection changes. When selectedFolderId is null
  // we're in the "All Scenarios" view and must refetch the release-wide list —
  // otherwise state.scenarios still holds the previously-selected folder's list.
  useEffect(() => {
    if (selectedFolderId !== null) {
      dispatch(fetchScenariosInFolderAsync(selectedFolderId))
    } else if (releaseId) {
      dispatch(fetchAllScenariosAsync(releaseId))
    }
  }, [dispatch, selectedFolderId, releaseId])

  // =========================================================================
  // URL QUERY PARAMETER SYNC
  // =========================================================================

  // Keep refs in sync with latest Redux state so the URL sync effect
  // can read current values without re-triggering on every state change
  useEffect(() => {
    selectedFolderIdRef.current = selectedFolderId
  }, [selectedFolderId])

  useEffect(() => {
    expandedFolderIdsRef.current = expandedFolderIds
  }, [expandedFolderIds])

  /**
   * Sync folder selection FROM URL query parameter on mount/URL change.
   * This is the SINGLE SOURCE OF TRUTH for dispatching selectFolder().
   *
   * Reads ?folder={id} from URL and:
   * 1. Validates the folder exists in the loaded folder tree
   * 2. Selects the folder in Redux state
   * 3. Auto-expands parent folders for nested folder deep-links
   * 4. Removes invalid ?folder param gracefully
   *
   * Uses refs for selectedFolderId and expandedFolderIds to avoid
   * re-running this effect when those values change (which would
   * cause duplicate API calls).
   */
  useEffect(() => {
    // Wait until folders are loaded before syncing
    if (foldersLoading || folders.length === 0) {
      return
    }

    const folderIdParam = searchParams.get('folder')

    // No folder param in URL — this is the "All Scenarios" view.
    // Align Redux if it still points at a previously-selected folder, otherwise
    // the sidebar row wouldn't get the selected styling and the panel wouldn't switch.
    if (!folderIdParam) {
      if (selectedFolderIdRef.current !== null) {
        dispatch(selectFolder(null))
      }
      return
    }

    const folderId = parseInt(folderIdParam, 10)

    // Validate folder exists
    const folderExists = folders.some(f => f.id === folderId)

    if (!folderExists) {
      // Invalid folder ID - remove param and log warning
      console.warn(`Folder ID ${folderId} from URL not found. Removing param.`)
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('folder')
      setSearchParams(newParams, { replace: true })
      return
    }

    // Folder exists - select it if not already selected
    if (selectedFolderIdRef.current !== folderId) {
      dispatch(selectFolder(folderId))

      // Auto-expand parent folders for nested folder deep-links
      const folderPath = buildFolderPath(folderId, folders)
      if (folderPath.length > 1) {
        // Get all ancestor folder IDs (excluding the current folder)
        const ancestorIds = folderPath.slice(0, -1).map(f => f.id)
        // Merge with existing expanded folders
        const currentExpanded = expandedFolderIdsRef.current || []
        const newExpandedIds = [...new Set([...currentExpanded, ...ancestorIds])]
        if (newExpandedIds.length !== currentExpanded.length) {
          dispatch(setExpandedFolderIds(newExpandedIds))
        }
      }
    }
  }, [searchParams, folders, foldersLoading, dispatch, setSearchParams])

  // =========================================================================
  // NAVIGATION HANDLERS
  // =========================================================================

  /**
   * Handle folder selection from sidebar.
   * Only updates the URL query parameter — the URL sync effect
   * is the single source of truth for dispatching selectFolder(),
   * which prevents duplicate API calls.
   *
   * @param {number} folderId - Folder ID to select
   */
  const handleFolderSelect = (folderId) => {
    // Only update URL - the URL sync effect will handle Redux state update.
    // folderId === null means the "All Scenarios" view; remove the param entirely.
    const newParams = new URLSearchParams(searchParams)
    if (folderId === null || folderId === undefined) {
      newParams.delete('folder')
    } else {
      newParams.set('folder', String(folderId))
    }
    setSearchParams(newParams, { replace: true })
  }

  // Handle scenario click - navigate to scenario detail
  // Stores navigation context for breadcrumb display in HeaderSection
  const handleScenarioClick = (scenarioId) => {
    // Build folder path for breadcrumb display using flat folders list
    const folderPath = buildFolderPath(selectedFolderId, folders)

    // Store navigation context for release scenario
    dispatch(setNavigationContext({
      isLibrary: false,
      currentRelease: currentRelease ? { id: currentRelease.id, name: currentRelease.name } : null,
      folderPath: folderPath
    }))

    navigate(`/releases/${releaseId}/scenario/${scenarioId}`)
  }

  // Handle navigation back to releases.
  // e.preventDefault() stops Atlaskit's default href="#" from interfering with React Router.
  const handleBackToReleases = (e) => {
    e?.preventDefault()
    navigate('/releases')
  }

  // =========================================================================
  // FOLDER CRUD HANDLERS
  // =========================================================================

  // Open create folder modal (optionally with parent)
  const handleCreateFolder = (parentId = null) => {
    setCreateFolderParentId(parentId)
    setIsCreateFolderModalOpen(true)
  }

  // Open edit folder modal
  const handleEditFolder = (folderId) => {
    const folder = folders.find(f => f.id === folderId)
    if (folder) {
      setEditingFolder(folder)
      setIsEditFolderModalOpen(true)
    }
  }

  // Open delete folder confirmation
  const handleDeleteFolder = (folderId) => {
    const folder = folders.find(f => f.id === folderId)
    if (folder) {
      setDeletingFolder(folder)
      setIsDeleteFolderModalOpen(true)
    }
  }

  // Confirm delete folder
  const handleConfirmDeleteFolder = async () => {
    if (deletingFolder?.id) {
      await dispatch(deleteFolderAsync(deletingFolder.id)).unwrap()
    }
  }

  // =========================================================================
  // SCENARIO CRUD HANDLERS
  // =========================================================================

  // Open create scenario modal
  const handleCreateScenario = () => {
    setIsCreateScenarioModalOpen(true)
  }

  // Open edit scenario modal
  const handleEditScenario = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId)
    if (scenario) {
      setEditingScenario(scenario)
      setIsEditScenarioModalOpen(true)
    }
  }

  // Open delete scenario confirmation
  const handleDeleteScenario = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId)
    if (scenario) {
      setDeletingScenario(scenario)
      setIsDeleteScenarioModalOpen(true)
    }
  }

  // Confirm delete scenario
  const handleConfirmDeleteScenario = async () => {
    if (deletingScenario?.id) {
      await dispatch(deleteTestScenarioAsync(deletingScenario.id)).unwrap()
      // Refresh folder tree to update scenario counts
      dispatch(fetchFolderTreeAsync(releaseId))
    }
  }

  // Open move scenario modal
  const handleMoveScenario = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId)
    if (scenario) {
      setMovingScenario(scenario)
      setIsMoveScenarioModalOpen(true)
    }
  }

  // Open import from library modal
  const handleImportFromLibrary = (targetFolderId = null) => {
    setImportTargetFolderId(targetFolderId)
    setIsImportFromLibraryModalOpen(true)
  }

  // Open import from release modal
  const handleImportFromRelease = (targetFolderId = null) => {
    setImportFromReleaseTargetFolderId(targetFolderId)
    setIsImportFromReleaseModalOpen(true)
  }

  // =========================================================================
  // CLONE HANDLERS
  // =========================================================================

  /**
   * Clone a folder with all its contents
   * Refreshes the folder tree after successful clone
   */
  const handleCloneFolder = async (folderId) => {
    try {
      await dispatch(cloneFolderAsync({ folderId, targetReleaseId: releaseId })).unwrap()
      // Refresh folder tree to show the cloned folder
      dispatch(fetchFolderTreeAsync(releaseId))
      showSuccess('Folder Cloned', 'Folder has been cloned successfully.')
    } catch (error) {
      showError('Clone Failed', error?.message || 'Failed to clone folder.')
    }
  }

  /**
   * Clone a test scenario with all its test cases and steps
   * Refreshes the scenario list after successful clone
   */
  const handleCloneScenario = async (scenarioId) => {
    try {
      await dispatch(cloneTestScenarioAsync(scenarioId)).unwrap()
      // Refresh scenarios to show the cloned scenario
      if (selectedFolderId !== null) {
        dispatch(fetchScenariosInFolderAsync(selectedFolderId))
      } else {
        dispatch(fetchAllScenariosAsync(releaseId))
      }
      showSuccess('Scenario Cloned', 'Test scenario has been cloned successfully.')
    } catch (error) {
      showError('Clone Failed', error?.message || 'Failed to clone scenario.')
    }
  }

  // =========================================================================
  // REORDER HANDLERS
  // =========================================================================

  // Handle scenario reordering (from drag-and-drop)
  const handleReorderScenarios = (reorderData) => {
    // Optimistically update UI
    dispatch(reorderScenariosOptimistic(reorderData))
    // Persist to backend
    dispatch(reorderScenariosAsync(reorderData))
  }

  // Handle folder reordering (from drag-and-drop)
  const handleReorderFolders = (reorderData) => {
    // Persist to backend (folder tree will be refetched)
    dispatch(reorderFoldersAsync(reorderData)).then(() => {
      // Refetch folder tree to get updated order
      dispatch(fetchFolderTreeAsync(releaseId))
    })
  }

  // =========================================================================
  // RENDER
  // =========================================================================

  // Loading state - shimmer skeleton mimicking the full two-panel layout
  if (releaseLoading || (foldersLoading && folderTree.length === 0)) {
    return <FolderBrowserSkeleton />
  }

  // Error state - uses blue background + centered content
  if (error) {
    return (
      <div
        className="folder-browser-page"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--ds-surface, #FFFFFF)'
        }}
      >
        <div className="folder-browser-wrapper" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: 'var(--ds-text-danger, #DE350B)' }}>Error loading data</p>
          <p style={{ color: 'var(--ds-text-subtlest, #6B778C)', fontSize: '14px' }}>{error}</p>
          <Button
            appearance="primary"
            onClick={() => {
              dispatch(fetchFolderTreeAsync(releaseId))
              dispatch(fetchAllScenariosAsync(releaseId))
            }}
            style={{ marginTop: '16px' }}
          >
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="folder-browser-page"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div
        className="page-header"
        style={{
          padding: '8px 20px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* Breadcrumb */}
        <Breadcrumbs>
          <BreadcrumbsItem
            text={selectedProject?.key || 'Project'}
            onClick={handleBackToReleases}
          />
          <BreadcrumbsItem
            text="Releases"
            onClick={handleBackToReleases}
          />
          {/* Current page — preventDefault stops Atlaskit's default href="#" from blanking the route. */}
          <BreadcrumbsItem
            text={currentRelease?.name || 'Release'}
            onClick={(e) => e.preventDefault()}
          />
        </Breadcrumbs>

        {/* Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/*
            Release-scoped report entry point. Mirrors the Project Report button
            on the release list page — a subtle button with the graph icon,
            disabled until we actually have a release id to hand to the modal.
          */}
          <Button
            appearance="subtle"
            iconBefore={<GraphLineIcon label="" size="small" />}
            onClick={handleOpenReleaseReport}
            isDisabled={!releaseId}
          >
            Release Report
          </Button>

          <Button
            appearance="primary"
            iconBefore={<AddIcon label="Add scenario" size="small" />}
            onClick={handleCreateScenario}
          >
            New Scenario
          </Button>
        </div>
      </div>

      {/* Two-Panel Layout with Unified Drag Context */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragEnd={handleUnifiedDragEnd}
      >
        {/* Blue background wrapper - matches Jira backlog/release page pattern */}
        <div className="folder-browser-wrapper">
          {/* White rounded card container for sidebar + scenario panel */}
          <div
            className="folder-browser-content"
          >
          {/* Left Panel - Folder Sidebar */}
          <FolderSidebar
            folderTree={folderTree}
            selectedFolderId={selectedFolderId}
            onFolderSelect={handleFolderSelect}
            onAddSubfolder={handleCreateFolder}
            onRenameFolder={handleEditFolder}
            onDeleteFolder={handleDeleteFolder}
            onCloneFolder={handleCloneFolder}
            onImportFromLibrary={handleImportFromLibrary}
            onImportFromRelease={handleImportFromRelease}
            onReorderFolders={handleReorderFolders}
            releaseId={releaseId}
          />

          {/* Right Panel - Scenarios */}
          <ScenarioPanel
            scenarios={scenarios}
            selectedFolderId={selectedFolderId}
            loading={scenariosLoading}
            onScenarioClick={handleScenarioClick}
            onCreateScenario={handleCreateScenario}
            onEditScenario={handleEditScenario}
            onDeleteScenario={handleDeleteScenario}
            onMoveScenario={handleMoveScenario}
            onCloneScenario={handleCloneScenario}
            onReorderScenarios={handleReorderScenarios}
            onImportFromLibrary={() => handleImportFromLibrary(selectedFolderId)}
            onImportFromRelease={() => handleImportFromRelease(selectedFolderId)}
            releaseId={releaseId}
          />
          </div>
        </div>
      </DndContext>

      {/* ===================================================================
          MODALS
          =================================================================== */}

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => {
          setIsCreateFolderModalOpen(false)
          setCreateFolderParentId(null)
        }}
        releaseId={releaseId}
        parentFolderId={createFolderParentId}
        jiraProjectId={selectedProject?.value}
      />

      {/* Create Scenario Modal */}
      <CreateScenarioModal
        isOpen={isCreateScenarioModalOpen}
        onClose={() => setIsCreateScenarioModalOpen(false)}
        releaseId={releaseId}
        folderId={selectedFolderId}
        jiraProjectId={selectedProject?.value}
      />

      {/* Edit Folder Modal */}
      <EditFolderModal
        isOpen={isEditFolderModalOpen}
        onClose={() => {
          setIsEditFolderModalOpen(false)
          setEditingFolder(null)
        }}
        folder={editingFolder}
      />

      {/* Edit Scenario Modal */}
      <EditScenarioModal
        isOpen={isEditScenarioModalOpen}
        onClose={() => {
          setIsEditScenarioModalOpen(false)
          setEditingScenario(null)
        }}
        scenario={editingScenario}
      />

      {/* Delete Folder Confirmation */}
      <ConfirmDeleteModal
        isOpen={isDeleteFolderModalOpen}
        onClose={() => {
          setIsDeleteFolderModalOpen(false)
          setDeletingFolder(null)
        }}
        onConfirm={handleConfirmDeleteFolder}
        title="Delete Folder"
        itemName={deletingFolder?.name || 'folder'}
        itemType="folder"
      />

      {/* Delete Scenario Confirmation */}
      <ConfirmDeleteModal
        isOpen={isDeleteScenarioModalOpen}
        onClose={() => {
          setIsDeleteScenarioModalOpen(false)
          setDeletingScenario(null)
        }}
        onConfirm={handleConfirmDeleteScenario}
        title="Delete Scenario"
        itemName={deletingScenario?.scenarioName || 'scenario'}
        itemType="scenario"
      />

      {/* Move Scenario Modal */}
      <MoveScenarioModal
        isOpen={isMoveScenarioModalOpen}
        onClose={() => {
          setIsMoveScenarioModalOpen(false)
          setMovingScenario(null)
        }}
        onMoveSuccess={() => {
          // Refresh folder tree to update scenario counts for both source and destination folders
          dispatch(fetchFolderTreeAsync(releaseId))
          // Refresh current folder's scenario list to remove the moved scenario
          if (selectedFolderId !== null) {
            dispatch(fetchScenariosInFolderAsync(selectedFolderId))
          } else {
            dispatch(fetchAllScenariosAsync(releaseId))
          }
        }}
        scenario={movingScenario}
        currentFolderId={selectedFolderId}
      />

      {/* Import from Library Modal */}
      <ImportFromLibraryModal
        isOpen={isImportFromLibraryModalOpen}
        onClose={() => {
          setIsImportFromLibraryModalOpen(false)
          setImportTargetFolderId(null)
        }}
        jiraProjectId={selectedProject?.value}
        releaseId={releaseId}
        targetFolderId={importTargetFolderId}
      />

      {/* Import from Release Modal */}
      <ImportFromReleaseModal
        isOpen={isImportFromReleaseModalOpen}
        onClose={() => {
          setIsImportFromReleaseModalOpen(false)
          setImportFromReleaseTargetFolderId(null)
        }}
        jiraProjectId={selectedProject?.value}
        currentReleaseId={releaseId}
        targetFolderId={importFromReleaseTargetFolderId}
      />

      {/* Release report — opened from the "Release Report" button in the header. */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={handleCloseReportModal}
        initialLevel="release"
        projectId={selectedProject?.value}
        releaseId={releaseId}
      />
    </div>
  )
}

export { FolderBrowser }
